import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {getScaledUrl} from "crqlar-react-common/dist/utils/media.util";
import {Box, Stack, Table} from "@mui/material";
import React, {ReactNode} from "react";
import {Media} from "crqlar-react-common/dist/models/common/media.model";

interface Props {
    itemIdentifier?: string
    title?: string | ReactNode
    subtitle?: string | ReactNode
    firstName?: string
    lastName?: string
    roomNumber?: string
    bottomSectionContent?: React.ReactNode
    singUI?: React.ReactNode
    tipUI?: React.ReactNode
    tableHead?: ReactNode
    tableBody?: ReactNode
    headerLogo?: Media
    showSignature?: boolean
    signatureText?: string
    showTip?: boolean
    tipText?: string
}

const GenericPrintoutPreview = (props: Props) => {
    const {t} = useLocalize()

    const logoUrl = props.headerLogo ? getScaledUrl(props.headerLogo, 1000, 1000) : null

    //region UI
    const headerSectionUI = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                alignSelf: 'center',
                gap: '12px'
            }}
        >
            {logoUrl && (
                <img
                    src={logoUrl}
                    alt={'Logo'}
                    style={{
                        width: '40mm',
                        objectFit: 'contain',
                        marginTop: '32px',
                    }}
                />
            )}

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'center',
                    gap: '2mm'
                }}
            >
                {props.itemIdentifier && (
                    <div
                        style={{
                            fontWeight: '500',
                            fontSize: '3mm',
                            textAlign: 'center'
                        }}
                    >
                        {props.itemIdentifier}
                    </div>
                )}
                {props.title && (
                    <div
                        style={{
                            fontWeight: 'bold',
                            fontSize: '5mm',
                            textAlign: 'center',
                            color: '#666666'
                        }}
                    >
                        {props.title}
                    </div>
                )}
                {props.subtitle && (
                    <div
                        style={{
                            fontWeight: 'italic',
                            fontSize: '4mm',
                            textAlign: 'center',
                            color: '#666666'
                        }}
                    >
                        {props.subtitle}
                    </div>
                )}
            </div>
        </div>
    )

    const nameAndRoomSectionUI = (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'center',
                gap: '8px',
                width: '100%'
            }}
        >
            <Stack
                direction={'row'}
                sx={{
                    fontSize: '20px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '@media print and (max-width: 160mm)': {
                        fontSize: '16px',
                    }
                }}
            >
                <div>
                    {[props.firstName, props.lastName].filter(Boolean).join(' ')}
                </div>

                <div>
                    {props.roomNumber ? `${props.roomNumber}` : ''}
                </div>
            </Stack>

            <div
                style={{
                    width: '100%',
                    height: '2px',
                    backgroundColor: 'black',
                    marginBottom: '3mm'
                }}
            />

            <Table
                sx={{
                    '@media print and (max-width: 160mm)': {
                        fontSize: '12px',
                    }
                }}
                style={{
                    width: '100%',
                }}
            >
                {props.tableHead}
                {props.tableBody}
            </Table>
        </div>
    )

    const signatureUI = props.showSignature ? (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2mm',
                minWidth: '60mm',
                alignItems: 'flex-end'
            }}
        >
            <div
                style={{
                    width: '60mm',
                    height: '1px',
                    backgroundColor: '#666',
                    marginTop: '10mm'
                }}
            />
            <div
                style={{
                    fontSize: '3mm',
                    color: '#666',
                }}
            >
                {props.signatureText || t('Signature')}
            </div>
        </div>
    ) : null

    const defaultTipUI = props.showTip ? (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2mm',
                minWidth: '60mm',
                alignItems: 'flex-end',
                marginRight: '8mm'
            }}
        >
            <div
                style={{
                    width: '60mm',
                    height: '1px',
                    backgroundColor: '#666',
                    marginTop: '10mm'
                }}
            />
            <div
                style={{
                    fontSize: '3mm',
                    color: '#666',
                }}
            >
                {props.tipText || t('Tip')}
            </div>
        </div>
    ) : null

    const bottomSectionUI = (
        <Box
            sx={{
                flexDirection: 'column',
                alignSelf: 'center',
                gap: '2mm',
                width: '100%'
            }}
        >
            <Box
                sx={{
                    fontWeight: '500',
                    fontSize: '5mm',
                    textAlign: 'center',
                    '@media print and (max-width: 160mm)': {
                        fontSize: '4mm',
                    }
                }}
            >
                {props.bottomSectionContent}
            </Box>

            <div
                style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'flex-end',
                    marginTop: '16mm'
                }}
            >
                {props.tipUI || defaultTipUI}
                {props.singUI || signatureUI}
            </div>
        </Box>
    )

    return (
        <Stack
            sx={{
                width: '100%',
                height: 'calc(100vh - 35mm)',
                gap: '20mm',
                maxWidth: 600,
                '@media print': {
                    maxWidth: '70%',
                    pageBreakAfter: 'auto',
                    pageBreakInside: 'avoid',
                    pageBreakBefore: 'auto'
                }
            }}
        >
            {headerSectionUI}
            {nameAndRoomSectionUI}
            {bottomSectionUI}
        </Stack>
    )
    //endregion UI
}

export default GenericPrintoutPreview